import { c as _c } from "react/compiler-runtime";
import React, { createContext, FunctionComponent, PropsWithChildren, useMemo, useState } from 'react';
import { prepareProps } from '../../utils/propUtils';
export const FixedContentContainerProviderContext = createContext<FixedContentContainerProviderValue>({
  containerElement: null,
  notificationContainerElement: null
});
export type FixedContentContainerProviderValue = {
  containerElement: HTMLElement | null;
  notificationContainerElement: HTMLElement | null;
};
export type FixedContentContainerProviderProps = PropsWithChildren<Record<never, never>>;

/**
 * This component should wrap the root of your application. It will provide the necessary data for all components to render fixed content correctly.
 * Without this components with fixed content will not work correctly if one of the parent containers creates a new stacking context (opacity, transform...).
 */
export const FixedContentContainerProvider: FunctionComponent<FixedContentContainerProviderProps> = React.memo(props => {
  const $ = _c(12);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    children
  } = t0;
  const [containerElement, setContainerElement] = useState(null);
  const [notificationContainerElement, setNotificationContainerElement] = useState(null);
  let t1;
  if ($[2] !== containerElement || $[3] !== notificationContainerElement) {
    t1 = {
      containerElement,
      notificationContainerElement
    };
    $[2] = containerElement;
    $[3] = notificationContainerElement;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const contextValue = t1;
  const t2 = contextValue.containerElement ? children : null;
  let t3;
  if ($[5] !== contextValue || $[6] !== t2) {
    t3 = <FixedContentContainerProviderContext.Provider value={contextValue}>{t2}</FixedContentContainerProviderContext.Provider>;
    $[5] = contextValue;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div ref={setContainerElement} data-testid="FixedContentContainerProvider.container" />;
    t5 = <div ref={setNotificationContainerElement} data-testid="FixedContentContainerProvider.notificationContainer" />;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3) {
    t6 = <>{t3}{t4}{t5}</>;
    $[10] = t3;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
});